import globals from "@homer.core/globals";
import application from "@homer.core/application";
import StorageCookie from "@homer.core/storage.cookie";
import user from "@homer.core/user";
import persistedEmail from "@homer.core/email";
import persistedPhone from "@homer.core/phone";
import entry from "@homer.core/entry";
import page from "@homer.core/page";
import attribution from "@homer.core/attribution";
import filterUndefined from "@homer.utility/object.filter.undefined";
import clientInfo from "@apfelkiste/client.info";
import scheduler from "@homer.core/scheduler";
import whenImages from "@homer.core/when.images";
import sink from "@apfelkiste/sink";
import device from "@apfelkiste/device";
import unifold from "@unifold/core";

const isInternal = new StorageCookie("@apfelkiste.internal", {
    sticky: false,
    encode: false,
});

unifold.set("isInternal", () => isInternal.get() === "true");
unifold.set("application", application);
unifold.set("client", clientInfo);
unifold.set("connector", () =>
    Promise.all([user, sink]).then(([user, data]) => {
        if (data.facebook) {
            // Keep raw Meta identity scoped to its connector; order details can override it downstream.
            data.facebook = {
                ...data.facebook,
                identity: filterUndefined({
                    email: user?.current?.email || undefined,
                    firstname: user?.current?.firstname || undefined,
                    lastname: user?.current?.lastname || undefined,
                    // Read the canonical number per event; Meta owns normalization and hashing downstream.
                    phone: persistedPhone.plain || undefined,
                }),
            };
        }
        data.klaviyo = (() => {
            if (attribution?._kx?.value) {
                return { _kx: attribution._kx.value };
            }

            if (user?.current) {
                return { email: user.current.email };
            }

            if (persistedEmail?.plain) {
                return { email: persistedEmail.plain };
            }

            return {};
        })();

        return data;
    }),
);
unifold.set("device", () => device.then((device) => device.identifier));
unifold.set("user", () => Promise.resolve(user).then((user) => user?.current?.hash || persistedEmail?.hash || undefined));
unifold.set("page", () => {
    const { uuid, url, id, type, label, layout, referrer, loadtime, breadcrumb } = page;

    return filterUndefined({
        uuid,
        url,
        id,
        type,
        label,
        layout,
        referrer,
        loadtime: typeof loadtime === "number" && loadtime >= 0 ? Math.round(loadtime) : undefined,
        breadcrumb,
    });
});
unifold.set("attribution", () => {
    return { ...attribution };
});
unifold.set("entry", entry);

scheduler.yield.low().then(() => {
    return globals.demand
        .request("@homer.core/client.state", "@unifold/event.engagement")
        .then(([clientState, EngagementEvent]) => {
            page.on(/^intent:(load|set)$/, () => {
                new EngagementEvent().dispatch();
            });

            clientState.on("leave", () => {
                new EngagementEvent().dispatch();
            });
        });
});

scheduler.yield.low(Promise.allSettled([whenImages])).then(() => {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            Promise.all([sink, device])
                .then(() => {
                    globals.demand
                        .request(
                            "@unifold/core",
                            "@apfelkiste/connector.sink",
                            "?@apfelkiste/connector.rtbhouse",
                            "?@apfelkiste/connector.remarketing",
                            "?@apfelkiste/connector.gtag",
                        )
                        .then(async ([unifold, ...connector]) => {
                            // Optional connectors may be disabled or blocked.
                            unifold.activate(...connector.filter((connector) => connector !== undefined));
                        });

                    resolve();
                })
                .catch(reject);
        }, 3000);
    });
});
